/**
 * Shared API configuration for the Athletics Data backend.
 * Most data endpoints require Firebase auth via getAuthHeaders().
 * A few public routes (e.g. landing contact-us) use fetch without auth.
 */
import { auth } from './firebase';

/**
 * API base URL. Uses same-origin when app is on Firebase Hosting (avoids CORS).
 * Set REACT_APP_API_URL for local dev (e.g. http://localhost:5001 when using firebase serve).
 */
function getApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    if (
      origin.includes('athletics-data-8fb35.web.app')
      || origin.includes('athletics-data-8fb35.firebaseapp.com')
      || origin.includes('trackiai.com')
    ) {
      return ''; // Same-origin: Hosting rewrites /api/** to backend
    }
    if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
      const localOverride = process.env.REACT_APP_API_URL;
      return (localOverride ? localOverride : 'http://localhost:5001').replace(/\/$/, '');
    }
  }
  const url = process.env.REACT_APP_API_URL;
  return url ? url.replace(/\/$/, '') : '';
}
export const API_BASE_URL = getApiBaseUrl();

/**
 * Parse a fetch Response as JSON; surface plain-text proxy errors (e.g. "Service Unavailable").
 */
export async function parseApiJson<T = Record<string, unknown>>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(text.trim() || `Request failed (${res.status})`);
  }
}

export async function getAuthHeaders(): Promise<{ Authorization: string } | Record<string, never>> {
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken(true);
      return { Authorization: `Bearer ${token}` };
    } catch (error) {
      console.error('Error getting auth token:', error);
      return {};
    }
  }
  return {};
}

export interface ClubOwnerCreatePayload {
  email: string;
  club_name: string;
  full_name?: string | null;
}

export interface ClubAthleteCreatePayload {
  email: string;
  full_name: string;
  date_of_birth: string;
  club_id?: number;
}

export interface AthleteSignupStartPayload {
  email: string;
  password: string;
  full_name: string;
  date_of_birth: string;
  requested_club_id?: number | null;
  wants_result_tagging: boolean;
  tag_request_note?: string | null;
}

export interface ClubMembershipReviewPayload {
  review_note?: string;
}

export async function postJsonWithAuth<T>(path: string, payload: unknown): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await res.json();
  if (!res.ok) {
    throw new Error(body?.error || `Request failed (${res.status})`);
  }
  return body as T;
}

export async function getJsonWithAuth<T>(path: string): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}${path}`, { headers });
  const body = await res.json();
  if (!res.ok) {
    throw new Error(body?.error || `Request failed (${res.status})`);
  }
  return body as T;
}

export async function postJsonPublic<T>(path: string, payload: unknown): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await res.json();
  if (!res.ok) {
    throw new Error(body?.error || `Request failed (${res.status})`);
  }
  return body as T;
}
